import { useState, useEffect, useCallback } from "react";
import { Box, Typography, IconButton, Alert } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import TableRowsOutlinedIcon from "@mui/icons-material/TableRowsOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import BarChartIcon from "@mui/icons-material/BarChart";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import GridViewOutlinedIcon from "@mui/icons-material/GridViewOutlined";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import { ArrowBack } from "@mui/icons-material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Cell,
  Treemap,
} from "recharts";

import AppDropdown from "../../common/app-dropdown";
import ButtonOne from "../../common/normal-button";
import DashboardBadge from "../../common/dashboard-badges";

import typography from "../../cssUtils/typography";
import {
  ColorsConstants,
  borderColors,
  grayFontColor,
  dashboardCardBgColor,
} from "../../cssUtils/constants";

import {
  fetchCampaignAnalytics,
  fetchSingleCampaignAnalytics,
  exportAnalyticsCSV,
} from "./analyticsApi";

// ─── Chart metric options ────────────────────────────────────────────────────
const CHART_METRICS = [
  { label: "Impressions", value: "impressions" },
  { label: "Reach", value: "reach" },
  { label: "Clicks (all)", value: "clicks" },
  { label: "Amount spent", value: "spend" },
  { label: "Conversations", value: "conversations" },
];

const BAR_COLORS = [
  ColorsConstants.primaryPurple100,
  "#4B7ACF",
  ColorsConstants.primaryBlue100,
  ColorsConstants.primaryPurple90,
  "#8CC3F0",
  "#1976D2",
];

// ─── Date preset options ─────────────────────────────────────────────────────
const DATE_PRESETS = [
  { label: "Today", value: "today" },
  { label: "Yesterday", value: "yesterday" },
  { label: "Last 7 days", value: "last_7d" },
  { label: "Last 14 days", value: "last_14d" },
  { label: "Last 30 days", value: "last_30d" },
  { label: "Last 90 days", value: "last_90d" },
  { label: "Last month", value: "last_month" },
  { label: "Maximum", value: "maximum" },
];

// ─── Currency formatter ──────────────────────────────────────────────────────
const fmtCurrency = (val, currency = "INR") => {
  if (val === null || val === undefined) return "—";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency,
    maximumFractionDigits: 2,
  }).format(val);
};

const fmtNumber = (val) => {
  if (val === null || val === undefined) return "—";
  return new Intl.NumberFormat("en-IN").format(val);
};

// ─── Summary badge strip ──────────────────────────────────────────────────────
const SummaryStrip = ({ totals, currency }) => {
  const items = [
    {
      label: "Total Reach",
      value: fmtNumber(totals?.reach),
      textColor: ColorsConstants.primaryBlue100,
    },
    {
      label: "Total Impressions",
      value: fmtNumber(totals?.impressions),
      textColor: ColorsConstants.primaryBlue100,
    },
    {
      label: "Total Clicks",
      value: fmtNumber(totals?.clicks),
      textColor: ColorsConstants.primaryPurple100,
    },
    {
      label: "Avg CPC",
      value: fmtCurrency(totals?.cpc, currency),
      textColor: ColorsConstants.primaryGray70,
    },
    {
      label: "Total Spent",
      value: fmtCurrency(totals?.spend, currency),
      textColor: ColorsConstants.primaryBlue100,
    },
    {
      label: "Conversations",
      value: fmtNumber(totals?.conversations),
      textColor: "#008026",
    },
  ];

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(6, 1fr)",
        gap: 2,
        mb: 3,
      }}
    >
      {items.map((item) => (
        <DashboardBadge
          key={item.label}
          value={item.value}
          label={item.label}
          textColor={item.textColor}
        />
      ))}
    </Box>
  );
};

// ─── Bar chart tooltip ───────────────────────────────────────────────────────
const BarChartTooltip = ({ active, payload, isCurrency, currency }) => {
  if (!active || !payload?.length) return null;
  const { fullName, value } = payload[0].payload;
  const formatted = isCurrency
    ? new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency,
        maximumFractionDigits: 2,
      }).format(value)
    : new Intl.NumberFormat("en-IN").format(value);
  return (
    <Box
      sx={{
        background: "#fff",
        border: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`,
        borderRadius: "8px",
        p: 1.5,
        boxShadow: "0 2px 8px rgba(0,0,0,0.10)",
        maxWidth: 260,
      }}
    >
      <Typography sx={{ ...typography.bodySmall, fontWeight: 600, mb: 0.5 }}>
        {fullName}
      </Typography>
      <Typography
        sx={{
          ...typography.bodySmall,
          color: ColorsConstants.primaryPurple100,
        }}
      >
        {formatted}
      </Typography>
    </Box>
  );
};

// ─── Campaign bar chart ───────────────────────────────────────────────────────
const CampaignBarChart = ({ campaigns, metric, currency, loading }) => {
  const isCurrency = metric === "spend" || metric === "cpc";

  const data = campaigns.map((c) => ({
    name:
      c.campaign_name.length > 22
        ? c.campaign_name.slice(0, 20) + "…"
        : c.campaign_name,
    fullName: c.campaign_name,
    value: c.metrics?.[metric] ?? 0,
  }));

  const yTickFormatter = (v) => {
    const compact = new Intl.NumberFormat("en-IN", {
      notation: "compact",
      maximumFractionDigits: 1,
    }).format(v);
    return isCurrency ? `₹${compact}` : compact;
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: 380,
          border: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`,
          borderRadius: "10px",
        }}
      >
        <Typography sx={{ ...typography.bodySmall, color: grayFontColor }}>
          Loading…
        </Typography>
      </Box>
    );
  }

  if (campaigns.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: 380,
          border: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`,
          borderRadius: "10px",
        }}
      >
        <Typography sx={{ ...typography.bodySmall, color: grayFontColor }}>
          No campaign data found for the selected date range.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        border: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`,
        borderRadius: "10px",
        p: 3,
        backgroundColor: "#fff",
      }}
    >
      <ResponsiveContainer width="100%" height={380}>
        <BarChart
          data={data}
          margin={{
            top: 8,
            right: 24,
            left: 8,
            bottom: campaigns.length > 4 ? 72 : 40,
          }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            stroke={borderColors.cardBorderPrimaryGrayColor}
            vertical={false}
          />
          <XAxis
            dataKey="name"
            tick={{
              fontSize: 12,
              fontFamily: "'Poppins', sans-serif",
              fill: grayFontColor,
            }}
            angle={campaigns.length > 4 ? -35 : 0}
            textAnchor={campaigns.length > 4 ? "end" : "middle"}
            interval={0}
          />
          <YAxis
            tick={{
              fontSize: 12,
              fontFamily: "'Poppins', sans-serif",
              fill: grayFontColor,
            }}
            tickFormatter={yTickFormatter}
            width={72}
            axisLine={false}
            tickLine={false}
          />
          <RechartsTooltip
            content={
              <BarChartTooltip isCurrency={isCurrency} currency={currency} />
            }
            cursor={{ fill: dashboardCardBgColor }}
          />
          <Bar dataKey="value" radius={[4, 4, 0, 0]} maxBarSize={56}>
            {data.map((_, idx) => (
              <Cell
                key={`bar-cell-${idx}`}
                fill={BAR_COLORS[idx % BAR_COLORS.length]}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
};

// ─── Treemap ──────────────────────────────────────────────────────────────────

const TREEMAP_EFFICIENCY_COLORS = ["#27AE60", "#F39C12", "#E74C3C"];

const TreemapCell = ({
  x,
  y,
  width,
  height,
  name,
  value,
  efficiency,
  campaign,
  isCurrency,
  currency,
  onCampaignClick,
}) => {
  if (!width || !height || width < 4 || height < 4) return null;

  const ratio = Math.min(1, Math.max(0, efficiency || 0));
  const colorIdx = ratio < 0.33 ? 0 : ratio < 0.66 ? 1 : 2;
  const bg = TREEMAP_EFFICIENCY_COLORS[colorIdx];

  const fmtVal = isCurrency
    ? new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency,
        maximumFractionDigits: 0,
      }).format(value)
    : new Intl.NumberFormat("en-IN", {
        notation: "compact",
        maximumFractionDigits: 1,
      }).format(value);

  const maxChars = Math.max(4, Math.floor(width / 7));
  const label =
    name?.length > maxChars ? name.slice(0, maxChars - 1) + "…" : name;
  const showName = width > 48 && height > 28;
  const showValue = width > 72 && height > 50;

  return (
    <g
      onClick={() => onCampaignClick && onCampaignClick(campaign)}
      style={{ cursor: "pointer" }}
    >
      <rect
        x={x + 1}
        y={y + 1}
        width={width - 2}
        height={height - 2}
        fill={bg}
        rx={5}
      />
      {showName && (
        <text
          x={x + width / 2}
          y={y + height / 2 - (showValue ? 9 : 0)}
          textAnchor="middle"
          dominantBaseline="middle"
          style={{
            fill: "#fff",
            fontSize: Math.min(13, Math.max(9, width / 10)),
            fontFamily: "'Poppins', sans-serif",
            fontWeight: 600,
            pointerEvents: "none",
          }}
        >
          {label}
        </text>
      )}
      {showValue && (
        <text
          x={x + width / 2}
          y={y + height / 2 + 11}
          textAnchor="middle"
          dominantBaseline="middle"
          style={{
            fill: "rgba(255,255,255,0.85)",
            fontSize: 11,
            fontFamily: "'Poppins', sans-serif",
            pointerEvents: "none",
          }}
        >
          {fmtVal}
        </text>
      )}
    </g>
  );
};

const CampaignTreemap = ({
  campaigns,
  metric,
  currency,
  loading,
  onCampaignClick,
}) => {
  const isCurrency = metric === "spend" || metric === "cpc";
  const maxCpc = Math.max(...campaigns.map((c) => c.metrics?.cpc ?? 0), 0.01);

  const data = campaigns
    .map((c) => ({
      name: c.campaign_name,
      value: c.metrics?.[metric] ?? 0,
      campaign: c,
      efficiency: (c.metrics?.cpc ?? 0) / maxCpc,
    }))
    .filter((d) => d.value > 0);

  const empty = (msg) => (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: 380,
        border: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`,
        borderRadius: "10px",
      }}
    >
      <Typography sx={{ ...typography.bodySmall, color: grayFontColor }}>
        {msg}
      </Typography>
    </Box>
  );

  if (loading) return empty("Loading…");
  if (data.length === 0)
    return empty("No campaign data found for the selected date range.");

  return (
    <Box
      sx={{
        border: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`,
        borderRadius: "10px",
        p: 3,
        backgroundColor: "#fff",
      }}
    >
      {/* Legend */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mb: 2,
          flexWrap: "wrap",
        }}
      >
        <Typography sx={{ ...typography.bodySmall, color: grayFontColor }}>
          Rectangle size = selected metric · Color = CPC efficiency
        </Typography>
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 0.75, ml: "auto" }}
        >
          {[
            ["#27AE60", "Low CPC"],
            ["#F39C12", "Mid"],
            ["#E74C3C", "High CPC"],
          ].map(([c, l]) => (
            <Box
              key={l}
              sx={{ display: "flex", alignItems: "center", gap: 0.4 }}
            >
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "2px",
                  backgroundColor: c,
                }}
              />
              <Typography sx={{ fontSize: 11, color: grayFontColor }}>
                {l}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <ResponsiveContainer width="100%" height={400}>
        <Treemap
          data={data}
          dataKey="value"
          aspectRatio={4 / 3}
          isAnimationActive={false}
          content={(props) => (
            <TreemapCell
              x={props.x}
              y={props.y}
              width={props.width}
              height={props.height}
              name={props.name}
              value={props.value}
              campaign={props.campaign}
              efficiency={props.efficiency}
              isCurrency={isCurrency}
              currency={currency}
              onCampaignClick={onCampaignClick}
            />
          )}
        />
      </ResponsiveContainer>

      <Typography
        sx={{
          ...typography.bodySmall,
          color: grayFontColor,
          mt: 1.5,
          textAlign: "center",
        }}
      >
        Click any campaign to view its detailed ad set breakdown
      </Typography>
    </Box>
  );
};

// ─── Funnel ───────────────────────────────────────────────────────────────────

const FUNNEL_COLORS = [
  ColorsConstants.primaryPurple100,
  "#4B7ACF",
  "#8CC3F0",
  "#C6E1FA",
];

const CampaignFunnel = ({ metrics, currency, loading }) => {
  const empty = (msg) => (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: 320,
        border: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`,
        borderRadius: "10px",
      }}
    >
      <Typography sx={{ ...typography.bodySmall, color: grayFontColor }}>
        {msg}
      </Typography>
    </Box>
  );

  if (loading) return empty("Loading…");
  if (!metrics) return empty("No data available.");

  const allStages = [
    { key: "reach", label: "Reach", value: metrics.reach ?? 0 },
    {
      key: "impressions",
      label: "Impressions",
      value: metrics.impressions ?? 0,
    },
    { key: "clicks", label: "Clicks (all)", value: metrics.clicks ?? 0 },
    {
      key: "conversations",
      label: "Conversations",
      value: metrics.conversations ?? 0,
    },
  ];
  const stages = allStages.filter((s) => s.value > 0);
  const maxVal = stages[0]?.value || 1;

  return (
    <Box
      sx={{
        border: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`,
        borderRadius: "10px",
        p: 4,
        backgroundColor: "#fff",
      }}
    >
      <Typography
        sx={{
          ...typography.bodyLarge,
          fontWeight: 600,
          color: ColorsConstants.primaryBlue100,
          mb: 3,
        }}
      >
        Conversion funnel
      </Typography>

      {stages.map((stage, i) => {
        const widthPct = (stage.value / maxVal) * 100;
        const prevVal = stages[i - 1]?.value;
        const convRate =
          i > 0 && prevVal > 0
            ? ((stage.value / prevVal) * 100).toFixed(1)
            : null;

        return (
          <Box key={stage.key}>
            {convRate !== null && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 0.75 }}>
                <Typography
                  sx={{
                    fontSize: 11,
                    color: grayFontColor,
                    backgroundColor: dashboardCardBgColor,
                    px: 1.5,
                    py: 0.3,
                    borderRadius: 4,
                  }}
                >
                  ↓ {convRate}% conversion
                </Typography>
              </Box>
            )}

            <Box
              sx={{ display: "flex", alignItems: "center", gap: 2, mb: 0.5 }}
            >
              <Box sx={{ width: 130, flexShrink: 0, textAlign: "right" }}>
                <Typography
                  sx={{
                    ...typography.bodySmall,
                    color: grayFontColor,
                    textTransform: "uppercase",
                    fontSize: 10,
                    letterSpacing: "0.06em",
                  }}
                >
                  {stage.label}
                </Typography>
                <Typography
                  sx={{
                    ...typography.bodyLarge,
                    fontWeight: 700,
                    color: ColorsConstants.primaryBlue100,
                  }}
                >
                  {fmtNumber(stage.value)}
                </Typography>
              </Box>

              <Box
                sx={{
                  flex: 1,
                  height: 44,
                  backgroundColor: "#F0F5FF",
                  borderRadius: 1,
                  overflow: "hidden",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Box
                  sx={{
                    width: `${widthPct}%`,
                    height: "100%",
                    backgroundColor:
                      FUNNEL_COLORS[i] ||
                      FUNNEL_COLORS[FUNNEL_COLORS.length - 1],
                    borderRadius: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minWidth: widthPct > 0 ? 4 : 0,
                  }}
                >
                  {widthPct > 12 && (
                    <Typography
                      sx={{ color: "#fff", fontSize: 12, fontWeight: 600 }}
                    >
                      {widthPct.toFixed(0)}%
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        );
      })}

      {/* Spend summary footer */}
      <Box
        sx={{
          mt: 3,
          pt: 2,
          borderTop: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`,
          display: "flex",
          gap: 4,
        }}
      >
        {[
          {
            label: "Amount spent",
            value: fmtCurrency(metrics.spend, currency),
          },
          { label: "CPC (all)", value: fmtCurrency(metrics.cpc, currency) },
          {
            label: "Overall CTR",
            value:
              metrics.reach > 0
                ? `${((metrics.clicks / metrics.reach) * 100).toFixed(2)}%`
                : "—",
          },
        ].map(({ label, value }) => (
          <Box key={label}>
            <Typography
              sx={{
                ...typography.bodySmall,
                color: grayFontColor,
                fontSize: 11,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
              }}
            >
              {label}
            </Typography>
            <Typography
              sx={{
                ...typography.bodyLarge,
                fontWeight: 700,
                color: ColorsConstants.primaryBlue100,
                mt: 0.25,
              }}
            >
              {value}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

// ─── Actions breakdown ────────────────────────────────────────────────────────

const ACTION_LABELS = {
  link_click: "Link Clicks",
  post_reaction: "Post Reactions",
  landing_page_view: "Landing Page Views",
  omni_landing_page_view: "Omni Landing Page Views",
  "onsite_conversion.total_messaging_connection": "Messaging Connections",
  post_engagement: "Post Engagement",
  post_interaction_gross: "Post Interactions (Gross)",
  page_engagement: "Page Engagement",
  post_interaction_net: "Post Interactions (Net)",
  "onsite_conversion.messaging_first_reply": "First Replies",
  "onsite_conversion.post_net_like": "Net Post Likes",
  "onsite_conversion.messaging_conversation_started_7d":
    "Conversations Started",
};

const ActionsBreakdown = ({ actions }) => {
  if (!actions?.length) return null;

  return (
    <Box sx={{ mt: 3 }}>
      <Typography
        sx={{
          ...typography.bodyLarge,
          fontWeight: 600,
          color: ColorsConstants.primaryBlue100,
          mb: 1.5,
        }}
      >
        Actions breakdown
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
          gap: 1.5,
        }}
      >
        {actions.map(({ action_type, value }) => {
          const label =
            ACTION_LABELS[action_type] ||
            action_type
              .replace(/_/g, " ")
              .replace(/\b\w/g, (c) => c.toUpperCase());
          return (
            <Box
              key={action_type}
              sx={{
                border: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`,
                borderRadius: "8px",
                p: 2,
                backgroundColor: dashboardCardBgColor,
              }}
            >
              <Typography
                sx={{
                  fontSize: 11,
                  color: grayFontColor,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  mb: 0.5,
                  lineHeight: 1.3,
                }}
              >
                {label}
              </Typography>
              <Typography
                sx={{
                  ...typography.h4,
                  fontWeight: 700,
                  color: ColorsConstants.primaryBlue100,
                }}
              >
                {fmtNumber(value)}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════════════════════════
const CampaignAnalyticsPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [datePreset, setDatePreset] = useState("last_7d");
  const [viewMode, setViewMode] = useState("table"); // "table" | "chart"
  const [chartMetric, setChartMetric] = useState("impressions");

  // Drill-down: when set, renders SingleCampaignView instead
  const [selectedCampaign, setSelectedCampaign] = useState(null);

  // Row open state keyed by ID
  const [openCampaigns, setOpenCampaigns] = useState({});
  const [openAdsets, setOpenAdsets] = useState({});
  const [openAccounts, setOpenAccounts] = useState({});

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const loadData = useCallback(async () => {
    // if (!adAccountId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await fetchCampaignAnalytics({ datePreset });
      setData(result);
      // Auto-expand first campaign on first load
      if (result?.campaigns?.length > 0) {
        setOpenCampaigns({ [result.campaigns[0].campaign_id]: true });
      }
    } catch (err) {
      setError(err.message || "Failed to load analytics");
    } finally {
      setLoading(false);
    }
  }, [datePreset]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ── Toggle helpers ─────────────────────────────────────────────────────────
  const toggleCampaign = (id) =>
    setOpenCampaigns((prev) => ({ ...prev, [id]: !prev[id] }));

  const toggleAdset = (id) =>
    setOpenAdsets((prev) => ({ ...prev, [id]: !prev[id] }));

  const toggleAccount = (id) =>
    setOpenAccounts((prev) => ({ ...prev, [id]: !prev[id] }));

  // ── Export ─────────────────────────────────────────────────────────────────
  const handleExport = () => {
    if (!data?.campaigns) return;
    exportAnalyticsCSV(data.campaigns, data.meta?.currency || "INR");
  };

  const currency = data?.meta?.currency || "INR";
  const campaigns = data?.campaigns || [];
  const totals = data?.totals;
  const dateLabel =
    DATE_PRESETS.find((d) => d.value === datePreset)?.label || "Last 7 days";
  const metaDateStr = data?.meta?.date_start
    ? `${data.meta.date_start} – ${data.meta.date_stop}`
    : null;

  if (selectedCampaign) {
    return (
      <SingleCampaignView
        campaign={selectedCampaign}
        onBack={() => setSelectedCampaign(null)}
        currency={currency}
        initialDatePreset={datePreset}
      />
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* ── Page header ── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          mb: 3,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box>
          <Typography
            sx={{ ...typography.h1, color: ColorsConstants.primaryBlue100 }}
          >
            Campaign Analytics
          </Typography>
          <Typography
            sx={{ ...typography.bodySmall, color: grayFontColor, mt: 0.5 }}
          >
            Pivot table · Campaigns → Ad sets → Accounts → Ads
            {metaDateStr && ` · ${metaDateStr}`}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
          }}
        >
          {/* Date preset dropdown */}
          <AppDropdown
            startIcon={<CalendarTodayOutlinedIcon sx={{ fontSize: 16 }} />}
            menuItems={DATE_PRESETS}
            selectedValue={datePreset}
            onMenuSelect={(val) => setDatePreset(val)}
          >
            {dateLabel}
          </AppDropdown>

          {/* Refresh */}
          <ButtonOne
            variant="outlined"
            startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
            onClick={loadData}
            disabled={loading}
          >
            Refresh
          </ButtonOne>

          {/* Export */}
          <ButtonOne
            startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 16 }} />}
            onClick={handleExport}
            disabled={!data || loading}
          >
            Export CSV
          </ButtonOne>
        </Box>
      </Box>

      {/* ── Error banner ── */}
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2, ...typography.bodySmall }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* ── Summary badges ── */}
      {!loading && totals && (
        <SummaryStrip totals={totals} currency={currency} />
      )}

      {/* ── Pivot toolbar ── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          mb: 1.5,
        }}
      >
        {/* Left: context-sensitive controls */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {viewMode === "table" && (
            <ButtonOne
              variant="outlined"
              startIcon={<TableRowsOutlinedIcon sx={{ fontSize: 16 }} />}
              sx={{ height: 36 }}
              onClick={() => {
                if (Object.keys(openCampaigns).length > 0) {
                  setOpenCampaigns({});
                  setOpenAdsets({});
                  setOpenAccounts({});
                } else {
                  const all = {};
                  campaigns.forEach((c) => {
                    all[c.campaign_id] = true;
                  });
                  setOpenCampaigns(all);
                }
              }}
            >
              {Object.keys(openCampaigns).length > 0
                ? "Collapse all"
                : "Expand all"}
            </ButtonOne>
          )}

          {(viewMode === "chart" || viewMode === "treemap") && (
            <AppDropdown
              menuItems={CHART_METRICS}
              selectedValue={chartMetric}
              onMenuSelect={(val) => setChartMetric(val)}
            >
              {viewMode === "treemap" ? "Size by: " : ""}
              {CHART_METRICS.find((m) => m.value === chartMetric)?.label}
            </AppDropdown>
          )}

          <Typography
            sx={{ ...typography.bodySmall, color: grayFontColor, ml: 0.5 }}
          >
            {campaigns.length} campaign{campaigns.length !== 1 ? "s" : ""}
          </Typography>
        </Box>

        {/* Right: view mode toggle */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <ButtonOne
            variant={viewMode === "table" ? "contained" : "outlined"}
            startIcon={<TableChartOutlinedIcon sx={{ fontSize: 16 }} />}
            sx={{ height: 36 }}
            onClick={() => setViewMode("table")}
          >
            Table
          </ButtonOne>
          <ButtonOne
            variant={viewMode === "chart" ? "contained" : "outlined"}
            startIcon={<BarChartIcon sx={{ fontSize: 16 }} />}
            sx={{ height: 36 }}
            onClick={() => setViewMode("chart")}
          >
            Chart
          </ButtonOne>
          <ButtonOne
            variant={viewMode === "treemap" ? "contained" : "outlined"}
            startIcon={<GridViewOutlinedIcon sx={{ fontSize: 16 }} />}
            sx={{ height: 36 }}
            onClick={() => setViewMode("treemap")}
          >
            Treemap
          </ButtonOne>
        </Box>
      </Box>

      {/* ── Chart view ── */}
      {viewMode === "chart" && (
        <CampaignBarChart
          campaigns={campaigns}
          metric={chartMetric}
          currency={currency}
          loading={loading}
        />
      )}

      {/* ── Treemap view ── */}
      {viewMode === "treemap" && (
        <CampaignTreemap
          campaigns={campaigns}
          metric={chartMetric}
          currency={currency}
          loading={loading}
          onCampaignClick={setSelectedCampaign}
        />
      )}

      {/* ── Table view ── */}
      {viewMode === "table" && (
        <Box
          sx={{
            border: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`,
            borderRadius: "10px",
            overflow: "hidden",
          }}
        >
          <Box sx={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ backgroundColor: "#F9FAFB" }}>
                  {[
                    { label: "Campaign name", width: 240 },
                    { label: "Ad Delivery", width: 150 },
                    { label: "Reach", width: 110 },
                    { label: "Impressions", width: 120 },
                    { label: "Clicks (all)", width: 115 },
                    { label: "CPC (all)", width: 115 },
                    { label: "Amount spent", width: 135 },
                    { label: "Schedule", width: 200 },
                  ].map((col) => (
                    <th
                      key={col.label}
                      style={{
                        textAlign: "left",
                        padding: "14px 16px",
                        fontFamily: "'Lexend', 'Roboto', sans-serif",
                        fontSize: "14px",
                        fontWeight: 600,
                        color: ColorsConstants.primaryBlue100,
                        backgroundColor: "#F9FAFB",
                        borderBottom: `1.5px solid ${borderColors.cardBorderPrimaryGrayColor}`,
                        minWidth: col.width,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan={8}
                      style={{
                        textAlign: "center",
                        padding: "40px",
                        color: grayFontColor,
                        fontFamily: "'Lexend', 'Roboto', sans-serif",
                        fontSize: "14px",
                      }}
                    >
                      Loading...
                    </td>
                  </tr>
                ) : campaigns.length === 0 ? (
                  <tr>
                    <td
                      colSpan={8}
                      style={{
                        textAlign: "center",
                        padding: "40px",
                        color: grayFontColor,
                        fontFamily: "'Lexend', 'Roboto', sans-serif",
                        fontSize: "14px",
                      }}
                    >
                      No campaign data found for the selected date range.
                    </td>
                  </tr>
                ) : (
                  campaigns.map((campaign) => (
                    <PivotCampaignBlock
                      key={campaign.campaign_id}
                      campaign={campaign}
                      currency={currency}
                      open={!!openCampaigns[campaign.campaign_id]}
                      onToggle={() => toggleCampaign(campaign.campaign_id)}
                      openAdsets={openAdsets}
                      onToggleAdset={toggleAdset}
                      openAccounts={openAccounts}
                      onToggleAccount={toggleAccount}
                      onCampaignClick={setSelectedCampaign}
                    />
                  ))
                )}

                {/* Totals row */}
                {totals && (
                  <PivotTotalsRow
                    totals={totals}
                    currency={currency}
                    rowCount={campaigns.length}
                  />
                )}
              </tbody>
            </table>
          </Box>
        </Box>
      )}
    </Box>
  );
};

// ─── Native HTML pivot rows (avoids MUI Table nesting constraint) ─────────────

const cellStyle = (extra = {}) => ({
  padding: "11px 16px",
  fontFamily: "'Lexend', 'Roboto', sans-serif",
  fontSize: "14px",
  fontWeight: 400,
  borderBottom: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`,
  whiteSpace: "nowrap",
  verticalAlign: "middle",
  ...extra,
});

const PivotCampaignBlock = ({
  campaign,
  currency,
  open,
  onToggle,
  openAdsets,
  onToggleAdset,
  openAccounts,
  onToggleAccount,
  onCampaignClick,
}) => (
  <>
    {/* Campaign row */}
    <tr style={{ backgroundColor: dashboardCardBgColor }}>
      <td style={cellStyle()}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <button
            onClick={onToggle}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: 0,
              display: "flex",
              alignItems: "center",
              color: ColorsConstants.primaryBlue100,
            }}
          >
            {open ? "▾" : "▸"}
          </button>
          <span
            onClick={() => onCampaignClick && onCampaignClick(campaign)}
            style={{
              color: ColorsConstants.primaryPurple100,
              fontWeight: 600,
              cursor: "pointer",
              textDecoration: "none",
            }}
            onMouseEnter={(e) => (e.target.style.textDecoration = "underline")}
            onMouseLeave={(e) => (e.target.style.textDecoration = "none")}
          >
            {campaign.campaign_name}
          </span>
        </div>
      </td>
      <td style={cellStyle()}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              display: "inline-block",
              flexShrink: 0,
              backgroundColor:
                campaign.effective_status === "ACTIVE"
                  ? ColorsConstants.primaryPurple100
                  : campaign.effective_status === "PAUSED"
                    ? ColorsConstants.primaryGray40
                    : campaign.effective_status === "ERROR"
                      ? ColorsConstants.error
                      : ColorsConstants.primaryGray20,
            }}
          />
          <span style={{ fontSize: "12px", color: grayFontColor }}>
            {campaign.delivery_label}
          </span>
        </div>
      </td>
      <td style={cellStyle({ fontWeight: 600 })}>
        {fmtNumber(campaign.metrics?.reach)}
      </td>
      <td style={cellStyle({ fontWeight: 600 })}>
        {fmtNumber(campaign.metrics?.impressions)}
      </td>
      <td style={cellStyle({ fontWeight: 600 })}>
        {fmtNumber(campaign.metrics?.clicks)}
      </td>
      <td style={cellStyle({ fontWeight: 600 })}>
        {fmtCurrency(campaign.metrics?.cpc, currency)}
      </td>
      <td style={cellStyle({ fontWeight: 600 })}>
        {fmtCurrency(campaign.metrics?.spend, currency)}
      </td>
      <td style={cellStyle({ color: grayFontColor })}>—</td>
    </tr>

    {open && (
      <>
        {/* "All" aggregate sub-row */}
        <tr style={{ backgroundColor: "#FFFFFF" }}>
          <td style={cellStyle({ paddingLeft: 36 })}>
            <span style={{ color: grayFontColor }}>All</span>
          </td>
          <td style={cellStyle()} />
          <td style={cellStyle()}>{fmtNumber(campaign.metrics?.reach)}</td>
          <td style={cellStyle()}>
            {fmtNumber(campaign.metrics?.impressions)}
          </td>
          <td style={cellStyle()}>{fmtNumber(campaign.metrics?.clicks)}</td>
          <td style={cellStyle()}>
            {fmtCurrency(campaign.metrics?.cpc, currency)}
          </td>
          <td style={cellStyle()}>
            {fmtCurrency(campaign.metrics?.spend, currency)}
          </td>
          <td style={cellStyle({ color: grayFontColor })}>—</td>
        </tr>

        {/* AdSet rows */}
        {campaign.ad_sets?.map((adset) => (
          <PivotAdSetBlock
            key={adset.adset_id}
            adset={adset}
            currency={currency}
            open={!!openAdsets[adset.adset_id]}
            onToggle={() => onToggleAdset(adset.adset_id)}
            openAccounts={openAccounts}
            onToggleAccount={onToggleAccount}
          />
        ))}
      </>
    )}
  </>
);

const PivotAdSetBlock = ({
  adset,
  currency,
  open,
  onToggle,
  openAccounts,
  onToggleAccount,
  topLevel,
}) => (
  <>
    <tr style={{ backgroundColor: "#FFFFFF" }}>
      <td style={cellStyle({ paddingLeft: topLevel ? 16 : 36 })}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <button
            onClick={onToggle}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: 0,
              display: "flex",
              alignItems: "center",
              color: grayFontColor,
              fontSize: 12,
            }}
          >
            {open ? "▾" : "▸"}
          </button>
          <span
            style={{
              color: ColorsConstants.primaryPurple100,
              fontWeight: 500,
              cursor: "pointer",
            }}
          >
            {adset.adset_name}
          </span>
        </div>
      </td>
      <td style={cellStyle()} />
      <td style={cellStyle()}>{fmtNumber(adset.metrics?.reach)}</td>
      <td style={cellStyle()}>{fmtNumber(adset.metrics?.impressions)}</td>
      <td style={cellStyle()}>{fmtNumber(adset.metrics?.clicks)}</td>
      <td style={cellStyle()}>{fmtCurrency(adset.metrics?.cpc, currency)}</td>
      <td style={cellStyle()}>{fmtCurrency(adset.metrics?.spend, currency)}</td>
      <td style={cellStyle({ color: grayFontColor })}>—</td>
    </tr>

    {open &&
      adset.accounts?.map((account) => (
        <PivotAccountBlock
          key={account.account_id}
          account={account}
          currency={currency}
          open={!!openAccounts[account.account_id]}
          onToggle={() => onToggleAccount(account.account_id)}
        />
      ))}
  </>
);

const PivotAccountBlock = ({ account, currency, open, onToggle }) => (
  <>
    <tr style={{ backgroundColor: "#FAFBFF" }}>
      <td style={cellStyle({ paddingLeft: 60 })}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <button
            onClick={onToggle}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: 0,
              color: grayFontColor,
              fontSize: 12,
            }}
          >
            {open ? "▾" : "▸"}
          </button>
          <span
            style={{
              color: ColorsConstants.primaryPurple100,
              fontWeight: 500,
              cursor: "pointer",
            }}
          >
            {account.account_name}
          </span>
        </div>
      </td>
      <td style={cellStyle()} />
      <td style={cellStyle()}>{fmtNumber(account.metrics?.reach)}</td>
      <td style={cellStyle()}>{fmtNumber(account.metrics?.impressions)}</td>
      <td style={cellStyle()}>{fmtNumber(account.metrics?.clicks)}</td>
      <td style={cellStyle()}>{fmtCurrency(account.metrics?.cpc, currency)}</td>
      <td style={cellStyle()}>
        {fmtCurrency(account.metrics?.spend, currency)}
      </td>
      <td style={cellStyle({ color: grayFontColor })}>—</td>
    </tr>

    {open &&
      account.ads?.map((ad, idx) => (
        <PivotAdRow
          key={ad.ad_id}
          ad={ad}
          currency={currency}
          isLast={idx === account.ads.length - 1}
        />
      ))}
  </>
);

const PivotAdRow = ({ ad, currency, isLast }) => (
  <tr style={{ backgroundColor: "#FFFFFF" }}>
    <td
      style={cellStyle({
        paddingLeft: 84,
        borderBottom: isLast
          ? `2px solid ${borderColors.cardBorderPrimaryGrayColor}`
          : `1px solid ${borderColors.cardBorderPrimaryGrayColor}`,
      })}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span
          style={{
            width: 7,
            height: 7,
            borderRadius: "50%",
            display: "inline-block",
            flexShrink: 0,
            backgroundColor:
              ad.effective_status === "ACTIVE"
                ? ColorsConstants.primaryPurple100
                : ad.effective_status === "PAUSED"
                  ? ColorsConstants.primaryGray40
                  : ColorsConstants.primaryGray20,
          }}
        />
        <span
          style={{
            color: ColorsConstants.primaryPurple100,
            fontWeight: 400,
            cursor: "pointer",
          }}
        >
          {ad.ad_name}
        </span>
      </div>
    </td>
    <td style={cellStyle()}>
      <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
        <span
          style={{
            width: 7,
            height: 7,
            borderRadius: "50%",
            display: "inline-block",
            backgroundColor:
              ad.effective_status === "ACTIVE"
                ? ColorsConstants.primaryPurple100
                : ad.effective_status === "PAUSED"
                  ? ColorsConstants.primaryGray40
                  : ColorsConstants.primaryGray20,
          }}
        />
        <span style={{ fontSize: "12px", color: grayFontColor }}>
          {ad.delivery_label}
        </span>
      </div>
    </td>
    <td style={cellStyle()}>{fmtNumber(ad.metrics?.reach)}</td>
    <td style={cellStyle()}>{fmtNumber(ad.metrics?.impressions)}</td>
    <td style={cellStyle()}>{fmtNumber(ad.metrics?.clicks)}</td>
    <td style={cellStyle()}>{fmtCurrency(ad.metrics?.cpc, currency)}</td>
    <td style={cellStyle()}>{fmtCurrency(ad.metrics?.spend, currency)}</td>
    <td style={cellStyle({ color: ColorsConstants.primaryPurple100 })}>
      {ad.schedule || "—"}
    </td>
  </tr>
);

const PivotTotalsRow = ({ totals, currency, rowCount }) => (
  <tr
    style={{
      backgroundColor: ColorsConstants.primaryWhite,
      borderTop: `2px solid ${borderColors.cardBorderPrimaryGrayColor}`,
    }}
  >
    <td style={cellStyle()}>
      <div>
        <div style={{ fontWeight: 700, fontSize: "14px" }}>Total results</div>
        <div style={{ fontSize: "12px", color: grayFontColor }}>
          {rowCount} / {rowCount} rows displayed
        </div>
      </div>
    </td>
    <td style={cellStyle()} />
    <td style={cellStyle()}>
      <div style={{ fontWeight: 600 }}>{fmtNumber(totals?.reach)}</div>
      <div style={{ fontSize: "11px", color: grayFontColor }}>
        Meta Accounts
      </div>
    </td>
    <td style={cellStyle()}>
      <div style={{ fontWeight: 600 }}>{fmtNumber(totals?.impressions)}</div>
      <div style={{ fontSize: "11px", color: grayFontColor }}>Total</div>
    </td>
    <td style={cellStyle()}>
      <div style={{ fontWeight: 600 }}>{fmtNumber(totals?.clicks)}</div>
      <div style={{ fontSize: "11px", color: grayFontColor }}>Total</div>
    </td>
    <td style={cellStyle()}>
      <div style={{ fontWeight: 600 }}>
        {fmtCurrency(totals?.cpc, currency)}
      </div>
      <div style={{ fontSize: "11px", color: grayFontColor }}>Per Click</div>
    </td>
    <td style={cellStyle()}>
      <div style={{ fontWeight: 600 }}>
        {fmtCurrency(totals?.spend, currency)}
      </div>
      <div style={{ fontSize: "11px", color: grayFontColor }}>Total spent</div>
    </td>
    <td style={cellStyle({ color: grayFontColor })}>—</td>
  </tr>
);

// ─── Single campaign drill-down view ─────────────────────────────────────────

const SingleCampaignView = ({
  campaign,
  onBack,
  currency,
  initialDatePreset,
}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [datePreset, setDatePreset] = useState(initialDatePreset || "last_7d");
  const [viewMode, setViewMode] = useState("table");
  const [openAdsets, setOpenAdsets] = useState({});
  const [openAccounts, setOpenAccounts] = useState({});

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchSingleCampaignAnalytics({
        campaignId: campaign.campaign_id,
        datePreset,
      });
      setData(result);
      if (result?.ad_sets?.length > 0) {
        setOpenAdsets({ [result.ad_sets[0].adset_id]: true });
      }
    } catch (err) {
      setError(err.message || "Failed to load campaign analytics");
    } finally {
      setLoading(false);
    }
  }, [campaign.campaign_id, datePreset]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const toggleAdset = (id) =>
    setOpenAdsets((prev) => ({ ...prev, [id]: !prev[id] }));
  const toggleAccount = (id) =>
    setOpenAccounts((prev) => ({ ...prev, [id]: !prev[id] }));

  const adSets = data?.ad_sets || [];
  const campaignMetrics = data?.metrics;
  const actionsBreakdown = data?.actions_breakdown || [];
  const effectiveCurrency = data?.meta?.currency || currency || "INR";
  const metaDateStr = data?.meta?.date_start
    ? `${data.meta.date_start} – ${data.meta.date_stop}`
    : null;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          mb: 3,
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box>
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}
          >
            <IconButton size="small" onClick={onBack} sx={{ p: 0.5 }}>
              <ArrowBack fontSize="small" sx={{ color: grayFontColor }} />
            </IconButton>
            <Typography
              onClick={onBack}
              sx={{
                ...typography.bodySmall,
                color: grayFontColor,
                cursor: "pointer",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              All campaigns
            </Typography>
          </Box>
          <Typography
            sx={{ ...typography.h1, color: ColorsConstants.primaryBlue100 }}
          >
            {campaign.campaign_name || data?.campaign_name || "Campaign"}
          </Typography>
          <Typography
            sx={{ ...typography.bodySmall, color: grayFontColor, mt: 0.5 }}
          >
            {viewMode === "funnel"
              ? "Conversion funnel"
              : "Ad sets → Accounts → Ads"}
            {metaDateStr && ` · ${metaDateStr}`}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
          }}
        >
          <AppDropdown
            menuItems={DATE_PRESETS}
            selectedValue={datePreset}
            onMenuSelect={(val) => setDatePreset(val)}
          >
            {DATE_PRESETS.find((d) => d.value === datePreset)?.label}
          </AppDropdown>
          <ButtonOne
            variant="outlined"
            startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
            sx={{ height: 36 }}
            onClick={loadData}
          >
            Refresh
          </ButtonOne>
        </Box>
      </Box>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2, ...typography.bodySmall }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {!loading && campaignMetrics && (
        <SummaryStrip totals={campaignMetrics} currency={effectiveCurrency} />
      )}

      {/* ── View mode toolbar ── */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <ButtonOne
            variant={viewMode === "table" ? "contained" : "outlined"}
            startIcon={<TableChartOutlinedIcon sx={{ fontSize: 16 }} />}
            sx={{ height: 36 }}
            onClick={() => setViewMode("table")}
          >
            Table
          </ButtonOne>
          <ButtonOne
            variant={viewMode === "funnel" ? "contained" : "outlined"}
            startIcon={<FilterAltOutlinedIcon sx={{ fontSize: 16 }} />}
            sx={{ height: 36 }}
            onClick={() => setViewMode("funnel")}
          >
            Funnel
          </ButtonOne>
        </Box>
      </Box>

      {/* ── Funnel view ── */}
      {viewMode === "funnel" && (
        <CampaignFunnel
          metrics={campaignMetrics}
          currency={effectiveCurrency}
          loading={loading}
        />
      )}

      {/* ── Table view ── */}
      {viewMode === "table" && (
        <Box
          sx={{
            border: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`,
            borderRadius: "10px",
            overflow: "hidden",
          }}
        >
          <Box sx={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ backgroundColor: "#F9FAFB" }}>
                  {[
                    { label: "Ad Set name", width: 240 },
                    { label: "Ad Delivery", width: 150 },
                    { label: "Reach", width: 110 },
                    { label: "Impressions", width: 120 },
                    { label: "Clicks (all)", width: 115 },
                    { label: "CPC (all)", width: 115 },
                    { label: "Amount spent", width: 135 },
                    { label: "Schedule", width: 200 },
                  ].map((col) => (
                    <th
                      key={col.label}
                      style={{
                        textAlign: "left",
                        padding: "14px 16px",
                        fontFamily: "'Lexend', 'Roboto', sans-serif",
                        fontSize: "14px",
                        fontWeight: 600,
                        color: ColorsConstants.primaryBlue100,
                        backgroundColor: "#F9FAFB",
                        borderBottom: `1.5px solid ${borderColors.cardBorderPrimaryGrayColor}`,
                        minWidth: col.width,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan={8}
                      style={{
                        textAlign: "center",
                        padding: "40px",
                        color: grayFontColor,
                        fontFamily: "'Lexend', 'Roboto', sans-serif",
                        fontSize: "14px",
                      }}
                    >
                      Loading...
                    </td>
                  </tr>
                ) : adSets.length === 0 ? (
                  <tr>
                    <td
                      colSpan={8}
                      style={{
                        textAlign: "center",
                        padding: "40px",
                        color: grayFontColor,
                        fontFamily: "'Lexend', 'Roboto', sans-serif",
                        fontSize: "14px",
                      }}
                    >
                      No data found for the selected date range.
                    </td>
                  </tr>
                ) : (
                  adSets.map((adset) => (
                    <PivotAdSetBlock
                      key={adset.adset_id}
                      adset={adset}
                      currency={effectiveCurrency}
                      open={!!openAdsets[adset.adset_id]}
                      onToggle={() => toggleAdset(adset.adset_id)}
                      openAccounts={openAccounts}
                      onToggleAccount={toggleAccount}
                      topLevel
                    />
                  ))
                )}
                {!loading && campaignMetrics && (
                  <PivotTotalsRow
                    totals={campaignMetrics}
                    currency={effectiveCurrency}
                    rowCount={adSets.length}
                  />
                )}
              </tbody>
            </table>
          </Box>
        </Box>
      )}

      <ActionsBreakdown actions={actionsBreakdown} />
    </Box>
  );
};

export default CampaignAnalyticsPage;
